import Typography from "../../Utility/Typography";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import { SortableHeader } from "../../Utility/Sorting";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import IssueWorkItemRow from "./IssueWorkItemRow";

export default function IssueListTable({
  paginatedIssues,
  searchTerm,
  sortKey,
  sortDirection,
  handleSort,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  selectedIssues,
  onSelectIssue,
  projectSlug,
  currentProjectTab,
  navigate,
  onOpenWorkItemDetail,
  editingField,
  setEditingField,
  onDoubleClick,
  onInlineEdit,
  priorityOptionsForDropdown,
  statusOptions,
  assigneeOptions,
  allowStatusUpdate,
  getStatusText,
  onDeleteClick,
  canEditWorkItems = true,
  canDeleteWorkItems = true,
  /** When false, the trailing actions column (e.g. delete) is omitted entirely. */
  showActionsColumn = true,
  prioritySavingIssueId = null,
  assigneeSavingIssueId = null,
  workItemTypeOptions = null,
  constrainWorkColumn = false,
}) {
  const colCount = showActionsColumn ? 14 : 13;

  const handleRowClick = (issue) => (e) => {
    if (
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest(".context-menu") ||
      e.target.closest("button") ||
      e.target.closest(".relative") ||
      e.target.closest("[data-dropdown-portal]")
    ) {
      return;
    }
    const projectIdForUrl = projectSlug ?? issue.projectId ?? issue.project?.id;
    if (onOpenWorkItemDetail) {
      if (projectIdForUrl) {
        onOpenWorkItemDetail(issue.id, projectIdForUrl);
        return;
      }
      navigate(`/create-issue?id=${issue.id}`);
      return;
    }
    if (projectIdForUrl) {
      navigate(
        `/projects/${projectIdForUrl}?tab=${encodeURIComponent(currentProjectTab || "issues")}`
      );
    } else {
      navigate(`/create-issue?id=${issue.id}`);
    }
  };

  return (
    <div>
      <div className="w-full">
        <div className="relative flex flex-col min-w-0 bg-surface-primary shadow-sm transition-shadow">
          <div className="flex-1 p-2">
            <div className="live-preview">
              <div className="overflow-x-auto">
                <table className="w-full mb-0 clients-table">
                  <thead className="bg-surface-secondary">
                    <tr>
                      <th className="text-start align-middle px-3 py-2 w-12">
                        <Checkbox
                          name="selectAll"
                          checked={isAllSelected}
                          onChange={(e) => onSelectAll(e.target.checked)}
                          noWrapper={true}
                          style={{
                            opacity: isIndeterminate ? 0.5 : 1,
                          }}
                        />
                      </th>
                      <SortableHeader
                        columnKey="clientName"
                        label="Client"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="projectName"
                        label="Project"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="work"
                        label="Work"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        className={constrainWorkColumn ? "work-items-work-col" : ""}
                      />
                      <SortableHeader
                        columnKey="parentTitle"
                        label="Parent"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="type"
                        label="Task type"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="priority"
                        label={
                          <Typography
                            variant="span2"
                            className="flex items-center gap-1"
                            component="span"
                          >
                            Priority
                          </Typography>
                        }
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        sortable={false}
                      />
                      <SortableHeader
                        columnKey="assignee"
                        label="Assignee"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="reporter"
                        label="Reporter"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="status"
                        label={
                          <Typography
                            variant="span2"
                            className="flex items-center gap-1"
                            component="span"
                          >
                            Status
                          </Typography>
                        }
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        sortable={false}
                      />
                      <SortableHeader
                        columnKey="created"
                        label="Created"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                      />
                      <SortableHeader
                        columnKey="updated"
                        label="Updated"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        sortable={false}
                      />
                      <SortableHeader
                        columnKey="dueDate"
                        label="Due date"
                        onSort={handleSort}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        sortable={false}
                      />
                      {showActionsColumn ? (
                        <th className="text-start align-middle px-3 py-2 w-12"></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody className="relative">
                    {paginatedIssues.length > 0 ? (
                      paginatedIssues.map((issue) => (
                        <IssueWorkItemRow
                          key={issue.id}
                          issue={issue}
                          workItemTypeOptions={workItemTypeOptions}
                          projectSlug={projectSlug}
                          currentProjectTab={currentProjectTab}
                          navigate={navigate}
                          onOpenWorkItemDetail={onOpenWorkItemDetail}
                          selected={selectedIssues.has(issue.id)}
                          onSelectIssue={onSelectIssue}
                          onRowNavigate={handleRowClick(issue)}
                          editingField={editingField}
                          setEditingField={setEditingField}
                          onDoubleClick={onDoubleClick}
                          onInlineEdit={onInlineEdit}
                          priorityOptionsForDropdown={priorityOptionsForDropdown}
                          statusOptions={statusOptions}
                          assigneeOptions={assigneeOptions}
                          allowStatusUpdate={allowStatusUpdate}
                          getStatusText={getStatusText}
                          onDeleteClick={onDeleteClick}
                          canEditWorkItems={canEditWorkItems}
                          canDeleteWorkItems={canDeleteWorkItems}
                          showActionsColumn={showActionsColumn}
                          prioritySaving={prioritySavingIssueId === issue.id}
                          assigneeSaving={assigneeSavingIssueId === issue.id}
                          constrainWorkColumn={constrainWorkColumn}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={colCount} className="text-center">
                          <NoDataFound
                            message={
                              searchTerm
                                ? "No issues found matching your search"
                                : "No issues available"
                            }
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
